import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Property } from '../../mocks/properties';
import { formatNumber, getCurrentLocale } from '../../utils/propertyUtils';
import { useNearbyPlaces, type PlaceCategory } from '../../hooks/useNearbyPlaces';
import { PoiIcon } from '../feature/poiIcons';
import { deriveNeighborhoodFromNearby } from '../../utils/neighborhoodFromNearby';
import { scoreTint } from '../../services/neighborhoodMapService';

interface PropertyRatingsProps {
  property: Property;
  id?: string;
  className?: string;
  /** «عرض المزيد عن الحي» open or closed. Omit to let the section keep its own state (starts closed). */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * The rest of the area, revealed with the details: the property page passes
   * the location record and the registered-market cards (sale prices, rents).
   */
  children?: ReactNode;
}

// Match NearbyPlaces' radius so this card reuses the same cached Google fetch.
const NEARBY_RADIUS_M = 1500;

// Each factor wears the same line icon as its nearby-places category, so the
// score and the list below it read as one system.
const FACTOR_ICON: Record<string, PlaceCategory> = {
  schools: 'EDUCATION',
  services: 'SHOPPING',
  healthcare: 'HEALTHCARE',
  transport: 'TRANSPORTATION',
  religious: 'WORSHIP',
};

const H3 = 'm-0 text-[1.0625rem] font-semibold text-[var(--pv-color-text)]';

function FactorRow({ factorKey, label, score, locale }: { factorKey: string; label: string; score: number; locale: string }) {
  const icon = FACTOR_ICON[factorKey];
  return (
    <div className="flex items-center gap-3">
      <span className="flex-shrink-0 text-[var(--pv-color-primary)]">
        {icon ? <PoiIcon category={icon} size={22} /> : <i className="ri-map-pin-line text-[22px] leading-none" aria-hidden />}
      </span>
      <span className="w-[112px] flex-shrink-0 text-[0.9375rem] leading-snug text-[var(--pv-color-text)] sm:w-[160px]">{label}</span>
      <span className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--pv-color-surface-sunken)]">
        <span className="block h-full rounded-full bg-[var(--pv-color-primary)]" style={{ width: `${(score / 5) * 100}%` }} />
      </span>
      <b className="w-[36px] text-end text-[0.9375rem] font-semibold text-[var(--pv-color-text)] tabular-nums">{formatNumber(score, locale)}</b>
      <span className="sr-only">/ 5</span>
    </div>
  );
}

/**
 * The district section of the property page. Prefers LIVE nearby-places data
 * (Google Places, shared cache with the map); falls back to the listing's
 * extracted amenity flags (backend lib/neighborhood.js). No fabricated data
 * either way.
 *
 * Owner, 2026-10-07: the headline scores show first (the district's overall
 * score and the services around the property); «عرض المزيد عن الحي» opens the
 * rest: each service's score, the district score's source, and whatever the
 * page passes as children (the location record, sale prices, rents).
 */
export default function PropertyRatings({
  property,
  id,
  className = '',
  expanded,
  onExpandedChange,
  children,
}: PropertyRatingsProps) {
  const { t } = useTranslation(['property']);
  const locale = getCurrentLocale();
  const detailsId = useId();
  const [ownExpanded, setOwnExpanded] = useState(false);
  const isExpanded = expanded ?? ownExpanded;

  const { isReady, placesByCategory, getAllNearbyPlaces } = useNearbyPlaces({ searchRadius: NEARBY_RADIUS_M });
  const { latitude, longitude } = property;

  useEffect(() => {
    if (isReady && latitude && longitude) {
      getAllNearbyPlaces(latitude, longitude, NEARBY_RADIUS_M);
    }
  }, [isReady, latitude, longitude, getAllNearbyPlaces]);

  // Live data wins when available; otherwise use the listing-flag summary.
  const live = useMemo(() => deriveNeighborhoodFromNearby(placesByCategory), [placesByCategory]);
  const usingLive = live.hasData;
  const n = usingLive ? live : property.neighborhood;
  const captionKey = usingLive ? 'property.neighborhood.captionNearby' : 'property.neighborhood.caption';

  // District-wide composite (nightly job over the whole district's listings): a
  // DIFFERENT scope from the nearby-services score, so each carries its own
  // label, and its provenance shows with the details. Null → not shown.
  const districtScore = property.neighborhoodScore;
  const hasServices = Boolean(n?.hasData);
  const hasMore = hasServices || districtScore != null || Boolean(children);

  const toggle = () => {
    const next = !isExpanded;
    if (expanded === undefined) setOwnExpanded(next);
    onExpandedChange?.(next);
  };

  return (
    // A plain section: the property page gives it its hairline and spacing.
    <div id={id} className={`scroll-mt-28 ${className}`}>
      <h2 className="m-0 text-[1.25rem] font-semibold text-[var(--pv-color-primary)]">{t('property.neighborhood.title')}</h2>

      {/* The headline: the two scores, each with its own scope. */}
      {districtScore != null || hasServices ? (
        <dl className="m-0 mt-4 flex flex-wrap gap-x-12 gap-y-4">
          {districtScore != null && (
            <div>
              <dt className="text-[0.875rem] text-[var(--pv-color-text-muted)]">{t('property.neighborhood.districtScoreLabel')}</dt>
              <dd className="m-0 mt-1.5">
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[1rem] font-bold text-white"
                  style={{ backgroundColor: scoreTint(districtScore).fill }}
                >
                  <i className="ri-star-fill text-base leading-none" aria-hidden />
                  {formatNumber(districtScore, locale)}
                  <span className="font-semibold opacity-80">/ {formatNumber(5, locale)}</span>
                </span>
              </dd>
            </div>
          )}
          {hasServices && n && (
            <div>
              <dt className="text-[0.875rem] text-[var(--pv-color-text-muted)]">{t('property.neighborhood.nearbyServicesTitle')}</dt>
              <dd className="m-0 mt-1 flex items-baseline gap-1.5">
                <span className="text-[1.75rem] font-bold leading-none text-[var(--pv-color-primary)] tabular-nums">
                  {formatNumber(n.overallScore, locale)}
                </span>
                <span className="text-[0.9375rem] text-[var(--pv-color-text-muted)]">/ {formatNumber(5, locale)}</span>
              </dd>
            </div>
          )}
        </dl>
      ) : (
        <p className="m-0 mt-3 text-[0.9375rem] text-[var(--pv-color-text-muted)]">{t('property.neighborhood.empty')}</p>
      )}

      {hasMore && (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={isExpanded}
          aria-controls={detailsId}
          className="mt-5 inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-full text-[0.9375rem] font-semibold text-[var(--pv-color-primary)] underline-offset-4 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30"
        >
          {isExpanded ? t('property.neighborhood.showLess') : t('property.neighborhood.showMore')}
          <i
            className={`ri-arrow-down-s-line text-[1.25rem] leading-none transition-transform duration-150 ${isExpanded ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
      )}

      {/* The `hidden` attribute keeps the id for aria-controls while closed. */}
      <div id={detailsId} hidden={!isExpanded}>
        {isExpanded && (
          <div className="mt-4 space-y-8">
            {districtScore != null && (
              <p className="m-0 text-[0.875rem] leading-relaxed text-[var(--pv-color-text-muted)]">
                <span className="font-semibold text-[var(--pv-color-text)]">{t('property.neighborhood.districtScoreLabel')}: </span>
                {t('property.neighborhood.districtScoreCaption')}
              </p>
            )}

            {hasServices && n && (
              <div>
                <h3 className={H3}>{t('property.neighborhood.nearbyServicesTitle')}</h3>
                <p className="m-0 mt-1 text-[0.875rem] leading-relaxed text-[var(--pv-color-text-muted)]">{t(captionKey)}</p>
                <div className="mt-4 flex flex-col gap-3">
                  {n.factors.map((f) => (
                    <FactorRow key={f.key} factorKey={f.key} label={t(`property.neighborhood.factors.${f.key}`)} score={f.score} locale={locale} />
                  ))}
                </div>
              </div>
            )}

            {children}
          </div>
        )}
      </div>
    </div>
  );
}
